import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Button } from '../common/Catalyst/button';
import { AmenityAgreementReturn, AmenityReturn } from '../clients/rust-api-client';
import { amenityService } from '../clients/ApiClients';
import {
  formatMoment,
  reservationErrorMessage,
} from '../reservation/reservationMeta';
import { helpClass, inputClass, labelClass } from '../reservation/formStyles';
import { ErrorAlert } from '../common/Custom/ErrorAlert';

/**
 * The amenity's rental agreement, as immutable versions (#642).
 *
 * A published version is never edited: changing the agreement publishes
 * the next version, and every booking keeps the version its household
 * accepted. That is what makes "they agreed to the cleaning terms" a
 * fact rather than a guess about which text was on screen that day.
 */
interface Props {
  amenity: AmenityReturn;
  readOnly: boolean;
  onPublished: () => void;
}

export const AmenityAgreements: React.FC<Props> = ({ amenity, readOnly, onPublished }) => {
  const [versions, setVersions] = useState<AmenityAgreementReturn[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [writing, setWriting] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const list = (await amenityService.listAmenityAgreements({ amenityId: amenity.id })).data.agreements;
      setVersions([...list].sort((a, b) => b.version - a.version));
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't load the agreement versions."));
    }
  }, [amenity.id]);

  useEffect(() => {
    load();
  }, [load]);

  const startNew = () => {
    const current = versions[0];
    setTitle(current?.title ?? `${amenity.name} rental agreement`);
    setBody(current?.body ?? '');
    setWriting(true);
  };

  const publish = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await amenityService.publishAmenityAgreement({
        amenityId: amenity.id,
        amenityAgreementCreate: { title: title.trim(), body: body.trim() },
      });
      setWriting(false);
      await load();
      onPublished();
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't publish the agreement."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-4">
      <p className={helpClass}>
        {amenity.requires_agreement
          ? 'Households must accept the current version to book.'
          : 'Not required for booking — turn it on in Booking limits once a version is published.'}{' '}
        Publishing a change creates a new version; bookings keep the version they accepted.
      </p>

      {!readOnly && !writing && (
        <Button color="sky" onClick={startNew}>
          {versions.length ? 'Publish a new version' : 'Write the agreement'}
        </Button>
      )}

      {writing && (
        <form onSubmit={publish} className="space-y-3 rounded-lg border border-line p-4">
          <label className={labelClass}>
            Title
            <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} />
          </label>
          <label className={labelClass}>
            Text
            <textarea
              rows={10}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className={clsx(inputClass, 'resize-y')}
            />
          </label>
          <div className="flex gap-2">
            <Button color="sky" type="submit" disabled={saving || !title.trim() || !body.trim()}>
              {saving ? 'Publishing…' : `Publish version ${(versions[0]?.version ?? 0) + 1}`}
            </Button>
            <Button plain type="button" onClick={() => setWriting(false)} disabled={saving}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      <ErrorAlert>{error}</ErrorAlert>

      {versions.length === 0 ? (
        <p className="text-[13px] text-fg-muted">No agreement published yet.</p>
      ) : (
        <ul className="divide-y divide-line/60 rounded-lg border border-line">
          {versions.map((v, i) => (
            <li key={v.id} className="px-3 py-2.5 text-[13px]">
              <button
                type="button"
                onClick={() => setOpen(open === v.id ? null : v.id)}
                aria-expanded={open === v.id}
                className="flex w-full items-center gap-2 text-left"
              >
                <span className="font-medium text-fg">Version {v.version}</span>
                {i === 0 && (
                  <span className="rounded-full bg-accent/15 px-2 py-0.5 text-[11px] text-accent-fg">Current</span>
                )}
                <span className="min-w-0 flex-1 truncate text-fg-secondary">{v.title}</span>
                <span className="text-[12px] text-fg-muted">{formatMoment(v.published_at)}</span>
              </button>
              {open === v.id && (
                <p className="mt-2 whitespace-pre-line rounded-md bg-fg/[0.03] px-3 py-2 text-fg-secondary">
                  {v.body}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AmenityAgreements;
